// Modules
import moment from "moment";

// Interfaces
import { GetReservationsResult, ReservationType } from 'Interfaces/GetReservations';

enum SortType {
  DESTINATION = "destination",
  DATES = "dates",
  TRANSACTION_DATE = "transaction_date",
  RESERVATION_TYPE = "reservation_type",
};

const SORT_FIELDS = {
  [SortType.DESTINATION]: "resortName",
  [SortType.DATES]: "checkInDate",
  [SortType.TRANSACTION_DATE]: "transactionDate",
  [SortType.RESERVATION_TYPE]: "reservationType",
}

const RESERVATION_DETAILS = "reservationDetails"

enum SortDirection {
  ASC = 'ascending',
  DESC = 'descending',
}

var resType: ReservationType;

/**
 * Evaluate a sort for two reservations based on destination
 */
 function evalDestinationSort(destinationA: string, destinationB: string, sortDirection: SortDirection) {
  //remove special characters that may affect sorting (i.e. different apostrophes)
  let a_stripped = destinationA.replace(/[^a-zA-Z0-9\s]/g, "");
  let b_stripped = destinationB.replace(/[^a-zA-Z0-9\s]/g, "");
  
  let letterCompare = (a_stripped).localeCompare(b_stripped);
  if (sortDirection === SortDirection.DESC)
    letterCompare = (b_stripped).localeCompare(a_stripped);

  // Sort by letters in Usage Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Usage Types are identical
  return 0;
}

/**
 * Evaluate a sort for two reservations based on date
 */
function evalDateSort(dateA: string, dateB: string, sortDirection: SortDirection) {
  const firstDate = moment(dateA);
  const secondDate = moment(dateB);

  let dateCompare = firstDate.valueOf() - secondDate.valueOf();
  if (sortDirection === SortDirection.DESC)
    dateCompare = secondDate.valueOf() - firstDate.valueOf();

  // Date sort was successful
  if (dateCompare > 0) {
    return 1;
  } else if (dateCompare < 0){
    return -1;
  }

  // Dates are identical
  return 0;
}

/**
 * Evaluate a sort for two reservations based on transaction date
 */
 function evalTransactionDateSort(transactionDateA: string, transactionDateB: string, sortDirection: SortDirection) {
  const firstDateTime = transactionDateA.split('T')[1].split(':');
  const secondDateTime = transactionDateB.split('T')[1].split(':');

  const firstDate = moment(transactionDateA.split('T')[0]).set('hour', parseInt(firstDateTime[0])).set('minute', parseInt(firstDateTime[1])).set('second', parseInt(firstDateTime[2]));
  const secondDate = moment(transactionDateB.split('T')[0]).set('hour', parseInt(secondDateTime[0])).set('minute', parseInt(secondDateTime[1])).set('second', parseInt(secondDateTime[2]));


  let dateCompare = firstDate.valueOf() - secondDate.valueOf();
  if (sortDirection === SortDirection.DESC)
    dateCompare = secondDate.valueOf() - firstDate.valueOf();

  // Date sort was successful
  if (dateCompare > 0) {
    return 1;
  } else if (dateCompare < 0){
    return -1;
  }

  // Dates are identical
  return 0;
}

/**
 * Evaluate a sort for two reservations based on reservation type
 */
 function evalReservationTypeSort(reservationTypeA: string, reservationTypeB: string, sortDirection: SortDirection) {
  let letterCompare = (reservationTypeA).localeCompare(reservationTypeB);
  if (sortDirection === SortDirection.DESC)
    letterCompare = (reservationTypeB).localeCompare(reservationTypeA);

  // Sort by letters in Usage Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Usage Types are identical
  return 0;
}

/**
 * Sort function based on Destination
 *
 * The sort priorities are the following:
 *  1. Destination
 *  2. Date ASC or DESC depending on resType
 */
 function destinationSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Destination
  const sortResult = evalDestinationSort(
    a[RESERVATION_DETAILS][SORT_FIELDS[SortType.DESTINATION]],
    b[RESERVATION_DETAILS][SORT_FIELDS[SortType.DESTINATION]],
    sortDirection
  );

  // Destination sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  let dateSortDirection = SortDirection.DESC;
  if (resType === ReservationType.UPCOMING)
    dateSortDirection = SortDirection.ASC
  const sortDateResult = evalDateSort(
    a[RESERVATION_DETAILS][SORT_FIELDS[SortType.DATES]],
    b[RESERVATION_DETAILS][SORT_FIELDS[SortType.DATES]],
    dateSortDirection
  );

  // Date sort was successful
  if (sortDateResult > 0) {
    return 1;
  } else if (sortDateResult < 0) {
    return -1;
  }

  // Destinations are identical
  // Dates are identical
  return 0;
}

/**
 * Sort function based on Date (Check in date)
 *
 * The sort priorities are the following:
 *  1. Date
 *  2. Destination ASC
 */
 function dateSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Date
  const sortResult = evalDateSort(
    a[RESERVATION_DETAILS][SORT_FIELDS[SortType.DATES]],
    b[RESERVATION_DETAILS][SORT_FIELDS[SortType.DATES]],
    sortDirection
  );

  // Date sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  const sortDestinationResult = evalDestinationSort(
    a[RESERVATION_DETAILS][SORT_FIELDS[SortType.DESTINATION]],
    b[RESERVATION_DETAILS][SORT_FIELDS[SortType.DESTINATION]],
    SortDirection.ASC
  );

  // Destination sort was successful
  if (sortDestinationResult > 0) {
    return 1;
  } else if (sortDestinationResult < 0) {
    return -1;
  }

  // Dates are identical
  // Destinations are identical
  return 0;
}

/**
 * Sort function based on Transaction Date
 *
 * The sort priorities are the following:
 *  1. Date
 */
 function transactionDateSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Transaction Date
  const sortResult = evalTransactionDateSort(
    a[RESERVATION_DETAILS][SORT_FIELDS[SortType.TRANSACTION_DATE]],
    b[RESERVATION_DETAILS][SORT_FIELDS[SortType.TRANSACTION_DATE]],
    sortDirection
  );

  // Transaction Date sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Transaction Dates are identical
  return 0;
}

/**
 * Sort function based on Reservation Type
 *
 * The sort priorities are the following:
 *  1. Reservation Type
 *  2. Destination ASC
 */
 function reservationTypeSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Reservation Type
  const sortResult = evalReservationTypeSort(
    a[RESERVATION_DETAILS][SORT_FIELDS[SortType.RESERVATION_TYPE]],
    b[RESERVATION_DETAILS][SORT_FIELDS[SortType.RESERVATION_TYPE]],
    sortDirection
  );

  // Reservation Type sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  const sortDestinationResult = evalDestinationSort(
    a[RESERVATION_DETAILS][SORT_FIELDS[SortType.DESTINATION]],
    b[RESERVATION_DETAILS][SORT_FIELDS[SortType.DESTINATION]],
    SortDirection.ASC
  );

  // Destination sort was successful
  if (sortDestinationResult > 0) {
    return 1;
  } else if (sortDestinationResult < 0) {
    return -1;
  }

  // Reservation Types are identical
  // Destinations are identical
  return 0;
}

/**
 * sort Reservations
 *
 * This is fired when the reservations are first loaded, and when a column sort
 * icon is clicked.
 *
 * If a sort icon was clicked, the switch logic is used for the sort field
 * that was clicked. If it's the initial load, sortField and
 * sortDirection will not be passed as params, and the default case will be
 * used in the switch.
 */
function sortReservations(
  reservations: GetReservationsResult[],
  reservationType: ReservationType,
  sortField?: SortType,
  sortDirection?: SortDirection
): any[] {

  resType = reservationType;
  let dateSortDirection = SortDirection.DESC;
  if (resType === ReservationType.UPCOMING)
    dateSortDirection = SortDirection.ASC

  let sortFunction:any
  switch (sortField) {
    // Sort by Destination
    case SortType.DESTINATION:
      sortFunction = (a:any, b:any) => destinationSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Date
    case SortType.DATES:
      sortFunction = (a:any, b:any) => dateSortFunc(a, b, sortDirection ? sortDirection : dateSortDirection);
      break;

    // Sort by Transaction Date
    case SortType.TRANSACTION_DATE:
      sortFunction = (a:any, b:any) => transactionDateSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Reservation Type
    case SortType.RESERVATION_TYPE:
      sortFunction = (a:any, b:any) => reservationTypeSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // default sort
    default:
      sortFunction = (a:any, b:any) => dateSortFunc(a, b, sortDirection ? sortDirection : dateSortDirection);
  }

  // Return the sorted contracts
  return [...reservations].sort(sortFunction);
}

export {
  sortReservations,
  SortType,
  SortDirection,
};
